import { OpenImgContextProvider } from 'openimg/react'
import { useEffect, useRef } from 'react'
import {
	data,
	redirect,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useMatches,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import { CommandPalette } from './components/command-palette.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { FeedbackWidget } from './components/feedback-widget.tsx'
import { GlobalFooter } from './components/global-footer.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { SearchBar } from './components/search-bar.tsx'
import { SiteNavigation, SiteLogo, MobileNavigation } from './components/site-navigation.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import { href as iconsHref, Icon } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { UserDropdown } from './components/user-dropdown.tsx'
import {
	CommandPaletteProvider,
	useCommandPalette,
} from './hooks/use-command-palette.tsx'
import {
	ThemeSwitch,
	useOptionalTheme,
	useTheme,
} from './routes/resources+/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { cachified, lruCache } from './utils/cache.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getPublicEnv, getAuthenticatedEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { sanityClient } from './utils/sanity/client.ts'
import { type SiteSettings } from './components/site-navigation.tsx'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser } from './utils/user.ts'
import { initVimeoTracking, cleanupVimeoTracking } from './utils/video-tracking.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		// Inter / Inter Tight fonts (loaded here rather than via @import in
		// tailwind.css, which Tailwind v4 strips — see styles/tailwind.css)
		{ rel: 'preconnect', href: 'https://fonts.googleapis.com' },
		{
			rel: 'preconnect',
			href: 'https://fonts.gstatic.com',
			crossOrigin: 'anonymous',
		} as const,
		{
			rel: 'stylesheet',
			href: 'https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900&family=Inter+Tight:wght@200;300;400;500;600;700;800;900&display=swap',
		},
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '16x16 32x32 48x48',
		},
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{ title: data ? 'The Box' : 'Error | The Box' },
		{ name: 'description', content: `Your ZetaDisplay toolbox and knowledge hub` },
		{ property: 'og:image', content: '/og-image.png'},
	
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	// Skip expensive operations for health checks
	// Health checks should not trigger database queries or external API calls
	if (request.headers.get('X-Healthcheck') === 'true') {
		const honeyProps = await honeypot.getInputProps()
		return data({
			user: null,
			siteSettings: null,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getPublicEnv(),
			toast: null,
			honeyProps,
		})
	}

	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							username: true,
							department: true,
							country: true,
							image: { select: { objectKey: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}

	// Redirect to profile completion if department/country missing
	if (user && (!user.department || !user.country)) {
		const url = new URL(request.url)
		if (!url.pathname.startsWith('/profile-complete') && !url.pathname.startsWith('/logout')) {
			const redirectTo = url.pathname + url.search
			throw redirect(`/profile-complete?redirectTo=${encodeURIComponent(redirectTo)}`)
		}
	}

	// Fetch site settings for navigation
	const siteSettings = await time(
		() =>
			cachified({
				key: 'site-settings',
				cache: lruCache, // LRU-only to avoid SQLite write locks
				ttl: 1000 * 60 * 60 * 24, // 24 hours (navigation changes rarely)
				staleWhileRevalidate: 1000 * 60 * 60 * 72, // 3 days (serve stale during slow Sanity fetches)
				async getFreshValue() {
					const settings = await sanityClient.fetch<SiteSettings | null>(`
					*[_type == "siteSettings" && _id == "siteSettings"][0] {
						title,
						description,
						logo,
						logoText,
						zetaLogo,
						mainNavigation[] {
							title,
							hasSubMenu,
							directLink,
							subMenuSections[] {
								sectionType,
								sectionTitle,
								sectionDescription,
								"tools": tools[]-> {
									_id,
									title,
									slug,
									description,
									"icon": icon-> {
										_id,
										name,
										svgCode,
										category
									},
									customIcon,
									url,
									category,
									comingSoon,
									featured,
									order,
									published
								},
								links[] {
									title,
									description,
									url,
									icon,
									comingSoon
								}
							}
						},
						footerDescription,
						footerMenuColumns[] {
							columnTitle,
							menuItems[] {
								title,
								url,
								openInNewTab
							}
						},
						footerCopyright
					}
				`)
					return settings
				},
			}),
		{ timings, type: 'site settings', desc: 'fetch site settings' },
	)

	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			user,
			siteSettings,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: user ? getAuthenticatedEnv() : getPublicEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
	userContext,
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
	userContext?: { user_id: string; user_role: string; user_department: string; user_country: string }
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
				{/* User context must be pushed before GTM loads so it's available for the first pageview */}
				{userContext && (
					<script
						nonce={nonce}
						dangerouslySetInnerHTML={{
							__html: `window.dataLayer = window.dataLayer || [];window.dataLayer.push(${JSON.stringify(userContext)});`,
						}}
					/>
				)}
				{/* Google Tag Manager */}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-KC555F4B');`,
					}}
				/>
			</head>
			<body className="bg-background text-foreground">
				{/* Google Tag Manager (noscript) */}
				<noscript>
					<iframe
						src="https://www.googletagmanager.com/ns.html?id=GTM-KC555F4B"
						height="0"
						width="0"
						style={{ display: 'none', visibility: 'hidden' }}
					/>
				</noscript>
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	const user = data?.user
	const userContext =
		user && 'roles' in user
			? {
					event: 'user_context_set',
					user_id: user.id,
					user_role: user.roles.map((r) => r.name).join(','),
					user_department: user.department ?? '',
					user_country: user.country ?? '',
				}
			: undefined

	// Re-push user context on client-side navigations when user data changes
	const isInitialRender = useRef(true)
	useEffect(() => {
		if (isInitialRender.current) {
			isInitialRender.current = false
			return
		}
		if (userContext) {
			window.dataLayer?.push(userContext)
		}
	}, [userContext?.user_id, userContext?.user_role, userContext?.user_department, userContext?.user_country])

	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV} userContext={userContext}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const theme = useTheme()
	const matches = useMatches()
	const isOnSearchPage = matches.find((m) => m.id === 'routes/users+/index')
	const isShareRoute = matches.some((m) => m.id === 'routes/share.$shareId')
	const isAuthRoute = matches.some((m) => m.id.includes('_auth+'))
	const isCalculatorRoute = matches.some(
		(m) => m.id === 'routes/retail-media-calculator',
	)
	// The ROI calculator keeps the nav/footer on screen but hides them (and its
	// page hero) when printing, so the exported PDF is just the report.
	const isRoiCalculatorRoute = matches.some(
		(m) => m.id === 'routes/_app+/roi-calculator',
	)
	// Only match client portal hub routes (routes/hub+/*), not knowledge-hub
	const isHubRoute = matches.some((m) => m.id.startsWith('routes/hub+'))
	const isBrandPortalRoute = matches.some((m) =>
		m.id.startsWith('routes/brand+/'),
	)
	const searchBar = isOnSearchPage ? null : <SearchBar status="idle" />
	const { open, setOpen } = useCommandPalette()
	useToast(data.toast)

	// Global keyboard shortcuts
	useEffect(() => {
		const down = (e: KeyboardEvent) => {
			// Ignore if typing in an input, textarea, or contenteditable element
			const target = e.target as HTMLElement
			if (
				target.tagName === 'INPUT' ||
				target.tagName === 'TEXTAREA' ||
				target.contentEditable === 'true'
			) {
				return
			}

			if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
				e.preventDefault()
				setOpen(true)
			}
		}

		document.addEventListener('keydown', down)
		return () => document.removeEventListener('keydown', down)
	}, [setOpen])

	// Vimeo video tracking via postMessage
	useEffect(() => {
		initVimeoTracking()
		return () => cleanupVimeoTracking()
	}, [])

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<div className="flex min-h-screen flex-col justify-between">
				{!isShareRoute && !isAuthRoute && !isHubRoute && !isCalculatorRoute && !isBrandPortalRoute && (
					<header
						className={`container py-6${isRoiCalculatorRoute ? ' print:hidden' : ''}`}
					>
						<nav className="flex flex-wrap items-center justify-between gap-4 sm:flex-nowrap md:gap-8">
							<span className="w-8">
								<SiteLogo siteSettings={data.siteSettings ?? undefined} />
							</span>
							{user && <SiteNavigation siteSettings={data.siteSettings ?? undefined} />}
							<div className="ml-auto hidden max-w-sm flex-1 sm:block">
								{/*{searchBar}*/}
							</div>
							<div className="flex items-center gap-4">
								{user && (
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setOpen(true)}
										className="text-muted-foreground hover:text-foreground hover:bg-accent flex h-8 w-8 items-center justify-center rounded-md p-0 transition-colors"
										aria-label="Search"
									>
										<Icon name="magnifying-glass" size="md" />
									</Button>
								)}
								{user && (
									<Link
										prefetch="intent"
										to="/bookmarks"
										className="text-muted-foreground hover:text-foreground hover:bg-accent hidden h-8 w-8 items-center justify-center rounded-md p-0 transition-colors md:flex"
										aria-label="Bookmarks"
									>
										<Icon name="bookmark" size="md" />
									</Link>
								)}
								<div className="hidden md:block">
									<ThemeSwitch
										userPreference={data.requestInfo.userPrefs.theme}
									/>
								</div>

								{user ? (
									<div className="hidden md:block">
										<UserDropdown />
									</div>
								) : (
									<Button asChild variant="default" size="lg">
										<Link to="/login">Log In</Link>
									</Button>
								)}
								{user && (
									<MobileNavigation
										siteSettings={data.siteSettings ?? undefined}
										themePreference={data.requestInfo.userPrefs.theme}
									/>
								)}
							</div>

						</nav>
					</header>
				)}

				<div className="flex flex-1 flex-col">
					<Outlet />
				</div>

				{!isShareRoute && !isAuthRoute && !isHubRoute && !isCalculatorRoute && !isBrandPortalRoute && user && (
					<div className={isRoiCalculatorRoute ? 'print:hidden' : undefined}>
						<GlobalFooter siteSettings={data.siteSettings} />
					</div>
				)}
			</div>
			<CommandPalette open={open} onOpenChange={setOpen} />
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</OpenImgContextProvider>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()

	// Wrap with FeedbackWidget if enabled (only available for authenticated users)
	const env = data.ENV as ReturnType<typeof getAuthenticatedEnv>
	if (env.ENABLE_FEEDBACK_WIDGET === 'true' && user) {
		return (
			<HoneypotProvider {...data.honeyProps}>
				<CommandPaletteProvider>
					<FeedbackWidget>
						<App />
					</FeedbackWidget>
				</CommandPaletteProvider>
			</HoneypotProvider>
		)
	}

	return (
		<HoneypotProvider {...data.honeyProps}>
			<CommandPaletteProvider>
				<App />
			</CommandPaletteProvider>
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
